/**
 * La misma API que usa el POS.
 *
 * No hay un backend del almacén: hay **un** backend, y esto es otra de sus
 * caras. Todo lo que aquí se pide pasa por la misma sesión, los mismos permisos
 * y el mismo filtro por sucursal que un cobro hecho desde la caja.
 */

import { cantidadOpcional, normalizarCantidad } from './numeros';
import type { VariantFamily, VentasDePrendas } from './prendas';
import type { FilaDeCarga } from './cargaInicial';
import type {
  EntradaDeCuarentena,
  EquipoDeFrio,
  LecturaDeFrio,
  LineaDelLibro,
  MovimientoControlado,
  SaldoControlado,
} from './botica';

const BASE = '/api/v1';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
  }

  /** Si volver a intentarlo tiene sentido o si es un no definitivo. */
  get isRetryable(): boolean {
    return this.status === 0 || this.status >= 500;
  }
}

/**
 * Dónde vive la sesión mientras dure la pestaña.
 *
 * En memoria y no en `localStorage`: un guion inyectado en cualquier
 * dependencia no puede leer el token de un almacén que ve costos y proveedores.
 *
 * Y ya no cuesta tener que volver a entrar. El refresco vive en una cookie
 * `HttpOnly` que el navegador manda sola y ningún guion lee, así que al cargar
 * la página se pide una sesión nueva con ella —`recuperarSesion`— y quien
 * recarga sigue dentro. Las dos cosas a la vez: nada al alcance de un guion, y
 * la sesión sobrevive.
 */
let accessToken: string | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

/**
 * Qué hacer cuando la sesión ya no se puede renovar: volver a la entrada.
 *
 * Lo pone la aplicación al arrancar. Sin esto, al caducar el token cada
 * pantalla se quedaba enseñando «no autorizado» y la única salida era
 * recargar a mano.
 */
let alPerderLaSesion: () => void = () => {};

export function onSesionPerdida(accion: () => void): void {
  alPerderLaSesion = accion;
}

/**
 * Una sola renovación a la vez.
 *
 * Al caducar el token fallan a la vez las cuatro consultas de la portada. Si
 * cada una pidiera su renovación, la segunda llegaría con el refresco que la
 * primera ya gastó —es de un solo uso— y echaría a quien estaba trabajando.
 */
let renovando: Promise<boolean> | null = null;

async function renovarUnaVez(): Promise<boolean> {
  renovando ??= recuperarSesion()
    .then((sesion) => sesion !== null)
    .finally(() => {
      renovando = null;
    });
  return renovando;
}

/**
 * Salir de verdad: el servidor olvida el refresco y borra la cookie.
 *
 * Soltar el token en memoria no bastaba: la cookie seguía viva, y al recargar
 * —o al abrir la página otra persona en el mismo equipo del depósito— la
 * sesión volvía sola.
 */
export async function cerrarSesion(): Promise<void> {
  try {
    await request<void>('POST', '/auth/logout', {});
  } catch {
    // Sin red, la sesión se suelta igual en este equipo.
  }
  accessToken = null;
}

/**
 * Baja un fichero de la API.
 *
 * No vale un enlace normal: la sesión va en la cabecera `Authorization`, y un
 * `<a href>` no la lleva. Así que se pide con `fetch`, se hace un `blob` y se
 * dispara la descarga desde ahí.
 *
 * El `URL.revokeObjectURL` no es un detalle: sin él, cada exportación deja el
 * fichero entero en memoria hasta que se cierre la pestaña, y quien cuadra un
 * mes exporta diez veces.
 */
export async function descargar(path: string, nombre: string, cuerpo?: unknown): Promise<void> {
  // Con cuerpo va por POST: la hoja de etiquetas lleva la lista de productos,
  // que no cabe —ni debe ir— en la dirección.
  const pedir = () =>
    fetch(`${BASE}${path}`, {
      credentials: 'same-origin',
      signal: AbortSignal.timeout(PLAZO_DESCARGA_MS),
      ...(cuerpo === undefined ? {} : { method: 'POST', body: JSON.stringify(cuerpo) }),
      headers: {
        ...(accessToken === null ? {} : { authorization: `Bearer ${accessToken}` }),
        ...(cuerpo === undefined ? {} : { 'content-type': 'application/json' }),
      },
    });

  let respuesta = await pedir();
  // El mismo trato que el resto: un token caducado se renueva y se repite.
  if (respuesta.status === 401 && (await renovarUnaVez())) respuesta = await pedir();

  if (!respuesta.ok) {
    throw new ApiError(
      respuesta.status,
      'EXPORT_FAILED',
      'No se pudo exportar el fichero.',
    );
  }

  const enlace = document.createElement('a');
  const url = URL.createObjectURL(await respuesta.blob());

  enlace.href = url;
  enlace.download = nombre;
  document.body.append(enlace);
  enlace.click();
  enlace.remove();
  URL.revokeObjectURL(url);
}

/**
 * Los 32 primeros dígitos del SHA-256 del archivo.
 *
 * Es lo que el servidor espera como huella, y se calcula del contenido para
 * que la misma foto dé siempre la misma clave: volver a subirla no duplica
 * nada, y cambiarla produce una clave nueva en lugar de pisar la anterior —lo
 * que deja que un equipo dé por buena para siempre la que ya se bajó—.
 */
async function huellaDe(bytes: ArrayBuffer): Promise<string> {
  const resumen = await crypto.subtle.digest('SHA-256', bytes);

  return [...new Uint8Array(resumen)]
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('')
    .slice(0, 32);
}

export function hasSession(): boolean {
  return accessToken !== null;
}

/**
 * ¿Hay una sesión que recuperar de la cookie?
 *
 * Se llama una vez al arrancar. Devuelve la sesión si el navegador todavía
 * tiene el refresco, y nulo si no —que es lo normal la primera vez, y no es un
 * error que haya que enseñarle a nadie—.
 *
 * El refresco es de un solo uso: el servidor rota la cookie en la misma
 * respuesta, así que dos pestañas abiertas a la vez pueden pisarse. Es
 * aceptable —la segunda pide entrar— y es el precio de que un token robado
 * deje al ladrón y al dueño peleándose por la sesión, que es justo cuando se
 * nota el robo.
 */
export async function recuperarSesion(): Promise<Session | null> {
  try {
    const renovada = await request<{
      accessToken: string;
      permissions?: string[];
      user?: Session['user'];
      scope?: Omit<Session['scope'], 'permissions'>;
    }>('POST', '/auth/refresh', {});

    /*
     * Se arma la sesión con lo que llega, y no se da por hecha.
     *
     * El refresco no tiene la misma forma que la entrada: los permisos vienen
     * fuera del ámbito, y el usuario faltaba. Tomarlo tal cual dejaba
     * `session.user` vacío y la barra lateral, al leer el nombre, tiraba la
     * página entera: quien recargaba se quedaba mirando una pantalla en
     * blanco. Si falta algo, se pide entrar de nuevo, que es incómodo pero
     * funciona.
     */
    if (renovada.user === undefined || renovada.scope === undefined) {
      return null;
    }

    accessToken = renovada.accessToken;

    return {
      accessToken: renovada.accessToken,
      user: renovada.user,
      scope: { ...renovada.scope, permissions: renovada.permissions ?? [] },
    };
  } catch {
    return null;
  }
}

/**
 * Cuánto se espera al servidor antes de darlo por inalcanzable.
 *
 * Sin plazo, una petición con la red a medias se quedaba colgada hasta que el
 * navegador se rendía —minutos—, con la pantalla girando. Con él, se dice «no
 * hay conexión» y React Query reintenta lo que se pueda reintentar. La
 * descarga y la subida de una foto llevan más: mueven archivos enteros.
 */
const PLAZO_PETICION_MS = 15_000;
const PLAZO_DESCARGA_MS = 120_000;
const PLAZO_SUBIDA_MS = 60_000;

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  token?: string,
  yaRenovada = false,
): Promise<T> {
  let response: Response;

  const authorization = token ?? accessToken;

  try {
    response = await fetch(`${BASE}${path}`, {
      method,
      signal: AbortSignal.timeout(PLAZO_PETICION_MS),
      // Para que la cookie del refresco viaje. Es del mismo origen —Vite hace
      // de proxy en desarrollo y Traefik en producción—, así que esto no abre
      // nada a terceros.
      credentials: 'same-origin',
      headers: {
        ...(body === undefined ? {} : { 'content-type': 'application/json' }),
        ...(authorization === null || authorization === undefined
          ? {}
          : { authorization: `Bearer ${authorization}` }),
      },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
  } catch {
    // No se pudo preguntar. Es distinto de que el servidor haya dicho que no, y
    // se dice distinto: aquí reintentar sirve.
    throw new ApiError(0, 'UNREACHABLE', 'No hay conexión con el servidor.');
  }

  /*
   * El token dura minutos y la jornada del almacén, horas. Un 401 con sesión
   * abierta es casi siempre eso: se renueva con la cookie y se repite lo que
   * se pedía. Solo si no se puede renovar se vuelve a la entrada.
   */
  if (
    response.status === 401 &&
    !yaRenovada &&
    token === undefined &&
    accessToken !== null &&
    !path.startsWith('/auth/')
  ) {
    if (await renovarUnaVez()) return request<T>(method, path, body, token, true);

    accessToken = null;
    alPerderLaSesion();
  }

  if (response.status === 204) return undefined as T;

  const payload: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    const error = payload as { error?: CuerpoDelError };

    throw new ApiError(
      response.status,
      error?.error?.code ?? 'UNKNOWN',
      mensajeDe(error?.error),
    );
  }

  return payload as T;
}

/** Cómo se llama cada campo cuando hay que decir cuál está mal. */
const CAMPOS: Record<string, string> = {
  quantity: 'la cantidad',
  counted: 'lo contado',
  unitCost: 'el costo',
  expiresAt: 'el vencimiento',
  lotCode: 'el lote',
  minimum: 'el mínimo',
  maximum: 'el máximo',
  yieldQuantity: 'el rendimiento',
  wastePercentage: 'la merma',
  batches: 'las tandas',
  sku: 'el código',
  name: 'el nombre',
  pin: 'el PIN',
};

/**
 * Lo que trae `error` cuando el servidor dice que no.
 *
 * Es `ApiErrorSchema` del contrato (`backend/src/contracts/envelopes/error.ts`),
 * con todo opcional porque un proxy caído puede contestar cualquier cosa. Cada
 * problema de forma trae `field`, la ruta del campo con puntos en texto
 * (`lines.0.quantity`). Hasta el 4 nov aquí se leía `path` como lista, que el
 * servidor nunca mandó, y por eso nunca salía «Revisa la cantidad».
 */
interface CuerpoDelError {
  code?: string;
  message?: string;
  details?: { issues?: { field?: string; message?: string }[] };
}

/**
 * El mensaje de un error del servidor, con el campo que falló si es de forma.
 *
 * «La petición no tiene el formato esperado» no le dice a nadie qué corregir;
 * «Revisa la cantidad» sí. Del campo se toma el último nombre: en
 * `lines.0.quantity` lo que está mal es la cantidad, no las líneas.
 */
export function mensajeDe(error?: CuerpoDelError): string {
  const base = error?.message ?? 'No se pudo completar la operación.';
  const campos = (error?.details?.issues ?? [])
    .map((issue) => issue.field?.split('.').filter((parte) => !/^\d+$/.test(parte)).at(-1))
    .filter((campo): campo is string => campo !== undefined && campo !== '')
    .map((campo) => CAMPOS[campo] ?? campo);

  return campos.length === 0 ? base : `Revisa ${[...new Set(campos)].join(', ')}.`;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body: unknown) => request<T>('POST', path, body),
  put: <T>(path: string, body: unknown) => request<T>('PUT', path, body),

  /** Retirar del catálogo o quitar una foto. Nunca borra una fila de verdad. */
  delete: <T>(path: string) => request<T>('DELETE', path),
};

// ── Entrar ────────────────────────────────────────────────────────────────

/** Una sucursal donde esta persona puede trabajar, tal como la nombra el POS. */
export interface Branch {
  branchId: string;
  branchName: string;
  branchCode: string;
  companyId: string;
  companyTradeName: string;
}

export interface Identity {
  bootstrapToken: string;
  branches: Branch[];
  user: { id: string; fullName: string };
}

export interface Session {
  accessToken: string;
  user: { id: string; fullName: string; email: string | null };
  scope: {
    branchId: string;
    branchName: string;
    companyTradeName: string;
    permissions: string[];
    /**
     * El rubro y los módulos del negocio, como los recibe el POS. Opcionales
     * porque un servidor anterior a los rubros no los manda, y entonces el
     * almacén se porta como restaurante con todo (`negocio.ts`).
     */
    businessType?: string;
    modules?: string[];
  };
}

/**
 * Paso uno: quién eres y en qué locales trabajas.
 *
 * Dos pasos y no uno porque un encargado de cadena tiene tres locales y el
 * almacén es de **uno**: preguntar cuál antes de abrir sesión evita que entre
 * al que no era y mueva stock del local equivocado.
 */
export function authenticateIdentity(command: {
  email: string;
  password: string;
  tenantSlug: string;
}): Promise<Identity> {
  return api.post<Identity>('/auth/identity', command);
}

/**
 * Canjea el código de un minuto con el que el celular abre el almacén ya
 * dentro (9 oct). El refresco se queda en la cookie, como al entrar con
 * contraseña; el código ya no sirve después de esto.
 */
export async function canjearCodigo(code: string): Promise<Session> {
  const abierta = await request<{
    accessToken: string;
    user: Session['user'];
    scope: Omit<Session['scope'], 'permissions'>;
    permissions?: string[];
  }>('POST', '/auth/handoff/exchange', { code });

  accessToken = abierta.accessToken;
  return {
    accessToken: abierta.accessToken,
    user: abierta.user,
    scope: { ...abierta.scope, permissions: abierta.permissions ?? [] },
  };
}

/** Paso dos: el local. Sin terminal: aquí no hay equipo dado de alta. */
export async function openSession(
  bootstrapToken: string,
  branchId: string,
): Promise<Session> {
  const abierta = await request<{
    accessToken: string;
    user: Session['user'];
    scope: Omit<Session['scope'], 'permissions'>;
    permissions?: string[];
  }>(
    'POST',
    '/auth/session',
    // `useCookie` es lo que le pide al servidor que se quede el refresco en
    // una cookie que este guion no puede leer, en vez de devolverlo aquí.
    { branchId, terminalId: null, useCookie: true },
    bootstrapToken,
  );

  // Los permisos llegan fuera del ámbito, igual que en la renovación. Se
  // meten dentro para que el resto de la aplicación los lea de un solo sitio:
  // el menú se arma con ellos, y sin esto quedaba vacío.
  return {
    accessToken: abierta.accessToken,
    user: abierta.user,
    scope: { ...abierta.scope, permissions: abierta.permissions ?? [] },
  };
}

// ── El almacén ────────────────────────────────────────────────────────────

export type StockLevel = 'out' | 'critical' | 'low' | 'ok' | 'over';
export type LotExpiry = 'expired' | 'expiring' | 'fresh';

export type MovementKind =
  | 'entry'
  | 'sale'
  | 'production'
  | 'waste'
  | 'transfer'
  | 'adjustment'
  | 'return'
  // Lo que un cliente devolvió y vuelve al estante (migración 198). Entra.
  | 'customer_return';

export type WasteReason =
  | 'expired'
  | 'natural_decay'
  | 'cold_chain_break'
  | 'preparation_error'
  | 'handling_break'
  | 'customer_return'
  | 'contamination'
  // Los de la bodega, que el celular ofrece al dar de baja (9 oct).
  | 'own_consumption'
  | 'theft'
  | 'other'
  // Lo pone el sistema al anular un plato que ya había descontado.
  | 'cancelled_dish';

export interface Supply {
  id: string;
  sku: string;
  name: string;
  category: string;
  unit: string;
  isLotTracked: boolean;
  /**
   * Sus lotes entran con vencimiento aunque el negocio no lo exija: lo
   * perecible. Opcional: un servidor de antes no lo manda.
   */
  requiresExpiry?: boolean;
  /**
   * Cada unidad lleva su serie o su IMEI: se recibe serie por serie (módulo
   * `serials`). Opcional: un servidor de antes no lo manda.
   */
  isSerialTracked?: boolean;

  /** El impreso en el envase, para escanearlo. Nulo en lo que va a granel. */
  barcode: string | null;

  /** Dónde está su foto, o nulo. Ya resuelta por el servidor. */
  imageUrl: string | null;

  supplier: string | null;
  /** La unidad en que se compra y cuántas de stock trae: un saco, 50 kg. */
  purchaseUnit: string | null;
  purchaseFactor: string | null;
  /** Cuántos días dura lo que se produce: el lote de cada tanda vence así. */
  shelfLifeDays: number | null;
  quantity: string;
  minimum: string;
  maximum: string;
  averageCost: string;
  value: string;
  location: string | null;
  level: StockLevel;
  reorderPoint: string;
  suggestedRestock: string;
  /**
   * Si está bajo su mínimo: solo con mínimo puesto. Es lo que decide la
   * reposición, no `level`, que con mínimo cero dice «agotado». Falta en un
   * servidor de antes: entonces se sigue por `level`.
   */
  belowMinimum?: boolean;
  /** Si se vendió más de lo registrado: saldo negativo, un descuadre. */
  oversold?: boolean;
  /**
   * Si se acabó y no tiene mínimo, y sí se había registrado: no es «bajo
   * mínimo», pero el pescado sin mínimo que se acabó tiene que verse.
   */
  outWithoutMinimum?: boolean;
  /**
   * Lo que separan las cotizaciones abiertas y vigentes (migración 275), en
   * la unidad del insumo: `"0.000"` si nada. Sigue en el libro —no se ha
   * vendido—, pero ya tiene dueño. Falta en un servidor de antes.
   */
  committed?: string;
  /** Avisa al llegar a cero aunque no tenga mínimo (migración 274). */
  alertAtZero?: boolean;
  /** Si alguna vez tuvo stock registrado. Falta en un servidor de antes. */
  everStocked?: boolean;
  /**
   * Los empaques en que llega: el rollo de 50 m y el de 100 m (migración 276).
   * `units` es cuántas unidades de stock trae. Falta en un servidor de antes.
   */
  packages?: SupplyPackage[];
}

export interface SupplyPackage {
  id: string;
  name: string;
  units: string;
  barcode: string | null;
}

export interface Lot {
  id: string;
  supplyId: string;
  supply: string;
  supplier: string | null;
  unit: string;
  code: string;
  quantity: string;
  unitCost: string;
  value: string;
  receivedAt: string;
  expiresAt: string | null;
  expiry: LotExpiry;
  expiryLabel: string;
}

export interface Movement {
  id: string;
  supplyId: string;
  supply: string;
  unit: string;
  lot: string | null;
  kind: MovementKind;
  quantity: string;
  balance: string;
  unitCost: string;
  value: string;
  document: string | null;
  reason: string | null;
  performedBy: string | null;
  occurredAt: string;
  /**
   * Cuándo se vendió de verdad, si no es cuando llegó al servidor: la venta que
   * la caja hizo sin red y subió después (migración 272). Opcional y solo
   * cuando difiere; un servidor de antes no lo manda.
   */
  soldAt?: string | null;
}

/** Lo que el negocio dice de sus lotes y de sus avisos. */
export interface LotSettings {
  requiresLotExpiry: boolean;
  expiryAlertDays: number;
  withholdsExpiredAtSale: boolean;
  /**
   * El correo del almacén sale en cuanto algo baja de su mínimo, y no con el
   * barrido. Opcional: un servidor de antes no lo manda.
   */
  immediateAlerts?: boolean;
  /**
   * Lo que vence antes de hoy + N no se vende (migración 273). Cero es lo de
   * siempre. Opcional: un servidor de antes no lo manda.
   */
  saleMinDaysToExpiry?: number;
}

/** Una venta por la que salió un lote. */
export interface LotSale {
  movementId: string;
  occurredAt: string;
  quantity: string;
  product: string;
  orderId: string;
  orderNumber: number | null;
  /** La línea se anuló después: su salida sigue en el libro, pero no llegó al cliente. */
  cancelled: boolean;
  /** Boletas y facturas del pedido, «B001-123». */
  receipts: string[];
  /** Solo con permiso para ver clientes; nulo si no, o si la venta no lo tenía. */
  customer: { name: string; documentNumber: string | null; phone: string | null } | null;
}

/** Lo que sabe el almacén de un código de lote, en este local. */
export interface LotTrace {
  code: string;
  lots: {
    id: string;
    code: string;
    supplyId: string;
    supply: string;
    unit: string;
    supplier: string | null;
    receivedAt: string;
    expiresAt: string | null;
    expiryLabel: string;
    quantity: string;
    sales: LotSale[];
    movements: Movement[];
  }[];
}

export interface Summary {
  totalValue: string;
  activeSupplies: number;
  belowMinimum: number;
  critical: number;
  expiringSoon: number;
  expired: number;
  valueAtRisk: string;
  /** Si el local registró alguna vez lo que tiene. Falta en un servidor de antes. */
  stockRecorded?: boolean;
  /** Cuántos quedaron en negativo: se vendió más de lo registrado. */
  oversold?: number;
  /** Cuántos se acabaron sin tener mínimo. */
  outWithoutMinimum?: number;
}

export interface RecipeLine {
  supplyId: string;
  supply: string;
  unit: string;
  quantity: string;
  unitCost: string;
  lineCost: string;

  /** Falso mientras ese insumo no haya entrado nunca al almacén. */
  hasCost: boolean;
}

export interface Recipe {
  id: string;
  productId: string | null;
  name: string;
  price: string;
  yieldQuantity: string;
  wastePercentage: number;
  ingredientsCost: string;
  wasteCost: string;
  cost: string;
  foodCost: number;
  margin: string;

  /** Cuántos ingredientes todavía no tienen costo, y por tanto no se cuentan. */
  ingredientsWithoutCost: number;

  /**
   * Cuántas porciones salen con lo que hay hoy.
   *
   * Avisa, no apaga: el plato sigue en la carta hasta que una persona lo
   * quite. El libro puede estar equivocado y sacar de la carta pescado que
   * está en la cámara es peor que venderlo.
   */
  possiblePortions: number;

  /** El ingrediente que primero se acaba, cuando no sale ninguna. */
  limitedBy: string | null;
  lines: RecipeLine[];
}

/**
 * Un extra de la carta —una opción de modificador— y su receta.
 *
 * «Doble pollo» se cobraba y no salía del almacén: el pollo de más se iba sin
 * que nadie lo descontara, y aparecía en el conteo como faltante.
 */
export interface ExtraRecipe {
  optionId: string;
  optionName: string;
  groupName: string;
  priceDelta: string;
  recipe: Recipe | null;
}

/**
 * El food cost real de un periodo. Importes en soles como texto, igual que el
 * resto de la API.
 */
export interface FoodCost {
  from: string;
  to: string;
  netSales: string;
  theoretical: string;
  real: string;
  byRecipe: string;
  staffMeals: string;
  /** Lo regalado al cliente: aparte, como la comida del personal. Falta en un servidor de antes. */
  courtesies?: string;
  waste: string;
  countAdjustments: string;
  purchases: string;
  theoreticalPercentage: number | null;
  realPercentage: number | null;
  /** Si se cerró algún conteo en el periodo. Sin él, lo real se queda corto. */
  wasCounted: boolean;
  lines: {
    supplyId: string;
    supply: string;
    unit: string;
    theoretical: string;
    real: string;
    difference: string;
  }[];
}

/** Algo que se vende y no descuenta nada porque nadie dijo de dónde sale. */
export interface PendingRecipe {
  productId: string;
  productName: string;
  timesSold: number;

  /** Falso en lo que se compra hecho: se resuelve vinculando, no cocinando. */
  requiresPreparation: boolean;

  /**
   * Si se compra hecho: se salda vinculando. Es lo que manda desde la
   * migración 184; `requiresPreparation` solo dice si sale comanda. Un
   * servidor de antes no lo manda.
   */
  isResale?: boolean;
}

/** Algo que empezó a faltar, con desde cuándo. */
export interface InventoryAlert {
  id: string;
  /** `expired_withheld`: se vendió cuando solo quedaba vencido (lote en la venta). */
  kind:
    | 'below_minimum'
    | 'lot_expiring'
    | 'expired_withheld'
    // Una serie que ya se había vendido o no es de aquí, y lo que va por serie
    // vendido sin decir cuál (migración 230). Se cierran a mano: «Revisado».
    | 'serial_conflict'
    | 'serial_missing'
    // Un modelo, sumando sus versiones, bajó de su mínimo (migración 274).
    | 'model_below_minimum';
  supplyId: string;
  supply: string;
  unit: string;
  lot: string | null;
  quantity: string;
  threshold: string;
  openedAt: string;
  /** Qué pasó, con la serie y el producto. Solo en los avisos de serie. */
  note?: string | null;
}

/** Un equipo con su serie, en el estante de este local o ya fuera. */
export interface SerialUnitView {
  serialNumber: string;
  condition: string;
  conditionLabel: string;
  status: 'in_stock' | 'sold' | 'returned' | 'written_off' | 'at_supplier' | 'in_repair' | 'on_display';
  statusLabel: string;
  inStock: boolean;
  unitCost: string;
  receivedAt: string;
  soldAt: string | null;
}

/**
 * Un equipo del local en «Equipos por serie» (8 oct): de todos los modelos,
 * con su modelo, su versión y dónde está —estante, vitrina, taller, proveedor—.
 */
export interface BranchSerialUnit {
  serialNumber: string;
  /** El IMEI 2 o la serie de fábrica (migración 274). */
  secondarySerialNumber: string | null;
  supplyId: string;
  supplyName: string;
  lotId: string;
  /** El producto de la carta que lo vende: «iPhone 15 · 256 GB · Negro». */
  productName: string | null;
  /** El modelo: la familia de la carta, «iPhone 15». */
  model: string | null;
  condition: string;
  conditionLabel: string;
  status: SerialUnitView['status'];
  statusLabel: string;
  /** Si está en la tienda con su lote en uno: el estante, la vitrina o el taller. */
  onShelf: boolean;
  unitCost: string;
  receivedAt: string;
  soldAt: string | null;
  returnedAt: string | null;
  updatedAt: string;
}

/** Contar por serie: lo leído contra lo que hay. No mueve el libro. */
export interface SerialCountResult {
  matched: string[];
  missing: { serialNumber: string; supplyId: string; status: SerialUnitView['status']; statusLabel: string }[];
  extra: {
    serialNumber: string;
    reason: 'unknown' | 'sold' | 'other_branch' | 'out_of_store' | 'other_model';
    reasonLabel: string;
    status: SerialUnitView['status'] | null;
  }[];
}

/** Un modelo con lo que le queda de todas sus versiones (migración 274). */
export interface ModelStock {
  familyId: string;
  name: string;
  versions: number;
  unit: string | null;
  quantity: string;
  minimum: string | null;
  belowMinimum: boolean;
}

/** El almacén de una cadena: dónde está la mercadería y dónde falta. */
export interface ChainStock {
  branches: { id: string; name: string; value: string }[];
  supplies: {
    supplyId: string;
    sku: string;
    name: string;
    unit: string;
    total: string;

    /** Por local. Ausente significa «aquí no se usa», que no es cero. */
    byBranch: Record<
      string,
      { quantity: string; minimum: string; short: boolean }
    >;
  }[];
  suggestions: {
    supplyId: string;
    supply: string;
    unit: string;
    from: string;
    fromName: string;
    to: string;
    toName: string;
    quantity: string;
    stillMissing: string;

    /** Lo que el local que recibe no tendrá que comprar. */
    savedValue: string;
  }[];
}

/** Lo que se fue sin que nada lo explique, en un período. */
export interface Shrinkage {
  from: string;
  to: string;
  missingValue: string;
  surplusValue: string;
  lines: {
    supplyId: string;
    supply: string;
    unit: string;
    explained: string;
    missing: string;
    surplus: string;

    /** Nulo cuando no hubo consumo: un porcentaje contra nada no es un número. */
    missingPercentage: number | null;

    missingValue: string;
  }[];
}

export interface Valuation {
  at: string;
  total: string;
  lines: {
    supplyId: string;
    sku: string;
    name: string;
    category: string;
    unit: string;
    quantity: string;
    unitCost: string;
    value: string;
  }[];
}

/**
 * La lista del mercado: cuánto comprar para mañana según lo que se vendió los
 * mismos días de las semanas anteriores, menos lo que hay y lo ya pedido.
 */
export interface MarketList {
  day: string;
  weeks: number;
  /** Los días que se miraron, para que el número no parezca inventado. */
  lookedAt: string[];
  groups: {
    supplierId: string | null;
    supplier: string;
    lines: {
      supplyId: string;
      name: string;
      unit: string;
      onHand: string;
      onOrder: string;
      average: string;
      suggested: string;
    }[];
  }[];
}

/** Qué pedir por lo que rota: lo vendido al día por los días a cubrir. */
export interface RotationList {
  days: number;
  coverDays: number;
  /** El primer y el último día mirados: hasta ayer. */
  from: string;
  to: string;
  groups: {
    supplierId: string | null;
    supplier: string;
    lines: {
      supplyId: string;
      name: string;
      unit: string;
      onHand: string;
      onOrder: string;
      dailyAverage: string;
      /** Para cuántos días alcanza lo que hay. */
      daysLeft: number | null;
      suggested: string;
    }[];
  }[];
}

/** Una orden por firmar: lo que hay que pedirle a alguien, ya calculado. */
export interface RestockGroup {
  /** Nulo cuando esos insumos no tienen a quién comprárselos. */
  supplierId: string | null;
  supplier: string;
  leadTimeDays: number;
  expectedAt: string;
  total: string;
  lines: {
    supplyId: string;
    sku: string;
    name: string;
    unit: string;
    quantity: string;
    suggested: string;
    minimum: string;
    unitCost: string;
    lineTotal: string;
  }[];
}

export interface Supplier {
  id: string;
  name: string;
  taxId: string | null;
  suppliesSummary: string | null;
  leadTimeDays: number;
  paymentTerms: string | null;
  minimumOrder: string | null;
  status: 'preferred' | 'active' | 'observed';

  /** A dónde se le manda la orden. Sin formato: hay celulares y fijos. */
  phone: string | null;

  /** «Marisol, la de pedidos». No es el nombre del proveedor. */
  contactName: string | null;
  supplyCount: number;
  lastReceivedAt: string | null;
}

/** Un conteo abierto. No trae lo que dice el libro: se cuenta a ciegas. */
export interface CountSession {
  id: string;
  document: string;
  /** Qué se cuenta: una categoría, una ubicación, o nulo si es todo. */
  scope: string | null;
  startedAt: string;
  startedBy: string;
  lines: {
    supplyId: string;
    sku: string;
    name: string;
    unit: string;
    category: string;
    location: string | null;
    barcode: string | null;
    /** Nulo mientras nadie lo ha contado. */
    counted: string | null;
    /**
     * Lo que va por lote, lote por lote, para contarlo así (migración 273).
     * Con alguno contado, el cierre ajusta cada lote y no el total. Un
     * servidor de antes no lo manda.
     */
    lots?: { lotId: string; code: string; expiresOn: string | null; counted: string | null }[];
  }[];
}

/** Lo contado contra la foto del libro, antes de cerrar. */
export interface CountReview {
  /** Cuántos insumos del conteo siguen sin contar: no se tocan al cerrar. */
  pending: number;
  lines: {
    supplyId: string;
    name: string;
    unit: string;
    expected: string;
    counted: string;
    difference: string;
    value: string;
    /** La línea es de un lote: su código y su vencimiento. */
    lotId?: string | null;
    lotCode?: string | null;
    lotExpiresOn?: string | null;
  }[];
}

/** Una sub-receta: consume insumos y devuelve otro al almacén. */
export interface Production extends Recipe {
  producesSupplyId: string;
  produces: string;
}

export type OrderStatus =
  | 'draft'
  | 'sent'
  | 'in_transit'
  | 'partial'
  | 'received'
  | 'cancelled';

export interface PurchaseOrder {
  id: string;
  number: string;
  status: OrderStatus;
  supplierId: string;
  supplier: string;

  /** A qué número se le manda. Nulo mientras nadie complete su ficha. */
  supplierPhone: string | null;

  /** «Marisol, la de pedidos». */
  supplierContact: string | null;

  /** A qué local se entrega. En una cadena no es un detalle. */
  branch: string;
  issuedAt: string | null;
  expectedAt: string | null;
  lineCount: number;
  total: string;
  lines: {
    supplyId: string;
    supply: string;
    unit: string;
    ordered: string;
    received: string;
    pending: string;
    unitCost: string;
  }[];
}

export interface CatalogOptions {
  /** Los demás locales del restaurante: a dónde se puede traspasar. */
  otherBranches: { id: string; name: string }[];
  units: { id: string; code: string; name: string }[];
  categories: { id: string; name: string }[];
  suppliers: { id: string; name: string }[];
}

/** Un plato de la carta, con si ya tiene receta. */
export interface SellableProduct {
  id: string;
  name: string;
  price: string;
  hasRecipe: boolean;

  /**
   * Si se prepara aquí. Lo dice la carta, no el almacén.
   *
   * Un pisco sour lleva escandallo aunque sea una bebida: la botella se gasta
   * de a poco en la barra. Una Inca Kola no: entra hecha y sale hecha.
   */
  requiresPreparation: boolean;

  /**
   * Si se compra hecho y se vende tal cual. Aparte de `requiresPreparation`
   * desde la migración 184: la Inca Kola va a la Barra —sale comanda— y es
   * reventa igual. Un servidor de antes no lo manda.
   */
  isResale?: boolean;
}

/**
 * Si el almacén lo trata como reventa: se vincula a su insumo en vez de
 * llevar receta. Con un servidor de antes de la 184, lo que no se prepara.
 */
export function esReventa(producto: {
  isResale?: boolean;
  requiresPreparation: boolean;
}): boolean {
  return producto.isResale ?? !producto.requiresPreparation;
}

/**
 * Cómo quedaría una fila de la carga inicial (`POST
 * /inventory/opening-stock/preview`). Docs/15 §2.39.
 */
export interface OpeningStockPreview {
  row: number;
  supplyId: string | null;
  supplyName: string | null;
  unitCode: string | null;
  via:
    | 'supply_barcode'
    | 'supply_sku'
    | 'product_barcode'
    | 'product_sku'
    | 'supply_name'
    | 'product_name'
    | null;
  /** En la unidad del almacén: tres cajas de cien son `"300.000"`. */
  quantity: string | null;
  unitCost: string | null;
  /** Cuántas unidades del almacén trae una de la hoja, si no es una. */
  unitsPerRow: string | null;
  currentStock: string | null;
  willLinkProduct: boolean;
  problems: string[];
}

export const almacen = {
  summary: () => api.get<Summary>('/inventory/summary'),

  /** Cómo quedaría cada fila de la hoja, sin escribir nada. */
  previewOpeningStock: (rows: FilaDeCarga[]) =>
    api
      .post<{ rows: OpeningStockPreview[] }>('/inventory/opening-stock/preview', { rows })
      .then((r) => r.rows),

  /**
   * Carga la hoja: una entrada `INVENTARIO-INICIAL` por fila, todo o nada y
   * una sola vez por `operationId`.
   */
  importOpeningStock: (command: { operationId: string; rows: FilaDeCarga[] }) =>
    api.post<{ imported: number }>('/inventory/opening-stock', command),

  /**
   * Las prendas con tallas y el insumo de cada talla, para verlas en matriz.
   * Un servidor de antes de las tallas no tiene la ruta: entonces no hay
   * prendas, y la pantalla se queda en la lista de siempre.
   */
  variantFamilies: () =>
    api
      .get<{ families: VariantFamily[] }>('/inventory/variant-families')
      .then((r) => r.families)
      .catch((error: unknown) => {
        if (error instanceof ApiError && error.status === 404) return [];
        throw error;
      }),

  /**
   * Lo vendido de cada talla entre dos días de Lima, neto de devoluciones.
   * El importe llega nulo sin el permiso de reportes.
   */
  /**
   * Lo que espera apartado en la sucursal, por producto (migración 275): lo
   * que la caja ya no ofrece libre. Un servidor de antes contesta 404, y la
   * cuadrícula sigue sin apartados.
   */
  heldUnits: () =>
    api.get<{ heldUnits: { productId: string; quantity: string }[] }>('/held-sales/units'),

  variantFamilySales: (from: string, to: string) =>
    api.get<VentasDePrendas>(`/inventory/variant-families/sales?from=${from}&to=${to}`),

  supplies: () =>
    api.get<{ supplies: Supply[] }>('/inventory/supplies').then((r) => r.supplies),

  /**
   * Los lotes con algo dentro. `alertDays` es con cuántos días de anticipación
   * un lote cuenta como «por vencer»; sin él, el servidor usa el margen del
   * negocio (`lotSettings`): cinco en la cocina, noventa en la botica.
   */
  lots: (supplyId?: string, alertDays?: number) => {
    const parametros = new URLSearchParams();
    if (supplyId !== undefined) parametros.set('supplyId', supplyId);
    if (alertDays !== undefined) parametros.set('alertDays', String(alertDays));
    const consulta = parametros.toString();

    return api
      .get<{ lots: Lot[] }>(`/inventory/lots${consulta === '' ? '' : `?${consulta}`}`)
      .then((r) => r.lots);
  },

  movements: (
    options: { kind?: MovementKind; supplyId?: string; from?: string; to?: string; limit?: number } = {},
  ) => {
    const query = new URLSearchParams();
    if (options.kind !== undefined) query.set('kind', options.kind);
    if (options.supplyId !== undefined) query.set('supplyId', options.supplyId);
    // Días de Lima, los dos incluidos.
    if (options.from !== undefined) query.set('from', options.from);
    if (options.to !== undefined) query.set('to', options.to);
    if (options.limit !== undefined) query.set('limit', String(options.limit));

    const sufijo = query.toString();

    return api
      .get<{ movements: Movement[] }>(
        `/inventory/movements${sufijo === '' ? '' : `?${sufijo}`}`,
      )
      .then((r) => r.movements);
  },

  /**
   * Rastrear un lote por el código de la caja: dónde está, su kardex y las
   * ventas por las que salió. 404 si este local no tiene ningún lote así.
   */
  traceLot: (code: string) =>
    api.get<LotTrace>(`/inventory/lots/trace?code=${encodeURIComponent(code.trim())}`),

  /**
   * Lo devuelto que espera en cuarentena y lo decidido en 30 días (migración
   * 240). Con el lote en la venta, nada devuelto vuelve al estante sin pasar
   * por aquí.
   */
  quarantine: () =>
    api.get<{ entries: EntradaDeCuarentena[] }>('/inventory/quarantine').then((r) => r.entries),

  /**
   * Liberarlo al estante o darlo de baja. Lo decide quien tiene
   * `inventory.approve`; una decisión tomada no se toma otra vez
   * (`QUARANTINE_ALREADY_DECIDED`).
   */
  decideQuarantine: (command: {
    quarantineId: string;
    decision: 'release' | 'write-off';
    operationId: string;
    note: string | null;
    /** Solo al liberar el sobrante de un conteo (migración 269). */
    lotCode?: string | null;
    expiresAt?: string | null;
  }) =>
    api.post<{ quarantineId: string; status: 'released' | 'written_off' }>(
      `/inventory/quarantine/${encodeURIComponent(command.quarantineId)}/${command.decision}`,
      {
        operationId: command.operationId,
        note: command.note,
        ...(command.lotCode === undefined ? {} : { lotCode: command.lotCode }),
        ...(command.expiresAt === undefined ? {} : { expiresAt: command.expiresAt }),
      },
    ),

  /** El libro de recetas entre dos días de Lima, los dos incluidos (migración 239). */
  prescriptionBook: (from: string, to: string) =>
    api
      .get<{ prescriptions: LineaDelLibro[] }>(
        `/inventory/prescriptions?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
      )
      .then((r) => r.prescriptions),

  /**
   * El libro de controlados entre dos días de Lima, con el saldo de cada uno
   * (migración 273). Lo que la ficha marca «Producto controlado».
   */
  controlledBook: (from: string, to: string) =>
    api.get<{ movements: MovimientoControlado[]; balances: SaldoControlado[] }>(
      `/inventory/controlled-book?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
    ),

  // La cadena de frío: los equipos y sus lecturas, en décimas de grado.
  coldStorage: () =>
    api.get<{ units: EquipoDeFrio[] }>('/inventory/cold-storage').then((r) => r.units),

  saveColdStorageUnit: (unit: { id: string; name: string; minTenths: number; maxTenths: number }) =>
    api.put<void>(`/inventory/cold-storage/${unit.id}`, {
      name: unit.name,
      minTenths: unit.minTenths,
      maxTenths: unit.maxTenths,
    }),

  /** Sin hora, la del servidor: ahora. Dice si quedó fuera de rango. */
  recordColdReading: (
    unitId: string,
    reading: { id: string; celsiusTenths: number; readAt?: string | null; note?: string | null },
  ) =>
    api.post<{ outOfRange: boolean; min: number; max: number }>(
      `/inventory/cold-storage/${unitId}/readings`,
      reading,
    ),

  coldReadings: (unitId: string, from: string, to: string) =>
    api
      .get<{ readings: LecturaDeFrio[] }>(
        `/inventory/cold-storage/${unitId}/readings?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
      )
      .then((r) => r.readings),

  /**
   * Recibir mercadería.
   *
   * `unitCost` va como texto, `"12.7119"`: ver `costoPorUnidadDeStock`.
   *
   * Si el lote ya llegó vencido el servidor se niega (`LOT_ALREADY_EXPIRED`):
   * casi siempre es la fecha mal tecleada. Con `confirmarVencido`, se le
   * pregunta a quien recibe con el mensaje del servidor y, si dice que sí, se
   * reenvía la misma operación confirmada —el rechazo no dejó nada—.
   */
  receive: async (
    command: {
      operationId: string;
      supplyId: string;
      quantity: string;
      unitCost: string;
      lotCode?: string;
      expiresAt?: string | null;
      document?: string;
      purchaseOrderId?: string | null;
      /** Quién lo trajo, cuando llega sin orden. Con orden manda la orden. */
      supplierId?: string | null;
      acceptExpired?: boolean;
      /** Llegó en duda: a cuarentena y no al estante (migración 273). */
      toQuarantine?: boolean;
    },
    confirmarVencido?: (mensaje: string) => boolean,
  ) => {
    const enviar = (cuerpo: typeof command) =>
      api.post<{
        movementId: string;
        balance: string;
        /**
         * En qué lote entró, si va por lote: el código, el vencimiento con que
         * quedó y si se sumó a uno que ya existía —que se queda con su fecha—.
         * Un servidor de antes no lo manda.
         */
        lot?: { code: string; expiresAt: string | null; addedToExisting: boolean } | null;
        /** Quedó en cuarentena: el saldo no cambió. */
        quarantined?: boolean;
      }>('/inventory/receipts', { ...cuerpo, quantity: normalizarCantidad(cuerpo.quantity) });

    try {
      return await enviar(command);
    } catch (error) {
      if (
        error instanceof ApiError &&
        error.code === 'LOT_ALREADY_EXPIRED' &&
        confirmarVencido?.(error.message) === true
      ) {
        return enviar({ ...command, acceptExpired: true });
      }
      throw error;
    }
  },

  /**
   * Recibir equipos serie por serie (módulo `serials`): cada uno entra como
   * su lote de uno, con su costo sin IGV y su estado. Una serie que ya es de
   * otro equipo del negocio se niega (`SERIAL_TAKEN`) y no entra ninguna.
   */
  receiveSerials: (command: {
    operationId: string;
    supplyId: string;
    unitCost: string;
    condition?: string;
    document?: string;
    purchaseOrderId?: string | null;
    units: {
      serialNumber: string;
      /** El IMEI 2 o la serie de fábrica, si la caja trae otro (migración 274). */
      secondarySerialNumber?: string | null;
      unitCost?: string;
      condition?: string;
    }[];
  }) =>
    api.post<{ received: number; balance: string; serials: string[] }>(
      '/inventory/serials/receipts',
      command,
    ),

  /** Encender o apagar el control por serie de un insumo, con el estante vacío. */
  setSerialTracking: (supplyId: string, serialTracked: boolean) =>
    api.put<void>(`/inventory/serials/supplies/${supplyId}`, { serialTracked }),

  /** Los equipos de un insumo en este local. */
  serialUnits: (supplyId: string) =>
    api
      .get<{ units: SerialUnitView[] }>(`/inventory/serials/supplies/${supplyId}`)
      .then((r) => r.units),

  /** Lo que «Asignar serie» puede arreglar de un aviso de serie. */
  serialAlertAssignment: (alertId: string) =>
    api.get<{
      alertId: string;
      supplyId: string;
      sales: {
        movementId: string;
        lineId: string;
        document: string | null;
        occurredAt: string;
        serialSaid: string | null;
      }[];
      units: { serialNumber: string; condition: string; conditionLabel: string }[];
    }>(`/inventory/serials/alerts/${alertId}/assignment`),

  /** «Asignar serie»: pone el equipo en la venta que salió sin él. */
  assignSerialToAlert: (alertId: string, command: { movementId: string; serialNumber: string }) =>
    api.post<{ resolved: boolean }>(`/inventory/serials/alerts/${alertId}/assign`, command),

  /** A la vitrina, al taller o de vuelta al estante. Sigue en el libro. */
  setSerialUnitStatus: (serialNumber: string, status: 'in_stock' | 'in_repair' | 'on_display') =>
    api.put<void>(`/inventory/serials/units/${encodeURIComponent(serialNumber)}/status`, { status }),

  /** El que el cliente devolvió dañado, al proveedor por garantía. */
  sendReturnedSerialToSupplier: (serialNumber: string) =>
    api.post<void>(`/inventory/serials/units/${encodeURIComponent(serialNumber)}/to-supplier`, {}),

  /** Los equipos del local, de todos los modelos, con filtros. */
  branchSerialUnits: (filtros: { statuses?: readonly string[]; supplyId?: string | null; q?: string }) => {
    const parametros = new URLSearchParams();
    if (filtros.statuses !== undefined && filtros.statuses.length > 0) parametros.set('status', filtros.statuses.join(','));
    if (filtros.supplyId) parametros.set('supplyId', filtros.supplyId);
    if (filtros.q !== undefined && filtros.q.trim() !== '') parametros.set('q', filtros.q.trim());
    const consulta = parametros.toString();
    return api
      .get<{ units: BranchSerialUnit[] }>(`/inventory/serials/units${consulta === '' ? '' : `?${consulta}`}`)
      .then((r) => r.units);
  },

  /** Contar por serie: lo leído contra lo que hay en la tienda. No mueve el libro. */
  countBySerial: (command: { supplyId?: string | null; serials: string[] }) =>
    api.post<SerialCountResult>('/inventory/serials/count', command),

  /** El IMEI 2 o la serie de fábrica de un equipo. Nulo lo quita. */
  setSecondarySerial: (serialNumber: string, secondarySerialNumber: string | null) =>
    api.put<void>(`/inventory/serials/units/${encodeURIComponent(serialNumber)}/secondary`, {
      secondarySerialNumber,
    }),

  /** Los modelos con lo que les queda de todas sus versiones y su mínimo. */
  modelStock: () => api.get<{ models: ModelStock[] }>('/inventory/models').then((r) => r.models),

  /** El mínimo de un modelo en este local. Nulo lo quita. */
  setModelMinimum: (familyId: string, minimum: string | null) =>
    api.put<void>(`/inventory/models/${familyId}/minimum`, {
      minimum: minimum === null ? null : normalizarCantidad(minimum, 'El mínimo del modelo'),
    }),

  /** «Revisado»: cierra un aviso de serie después de mirar el estante. */
  resolveSerialAlert: (alertId: string) =>
    api.post<void>(`/inventory/serials/alerts/${alertId}/resolve`, {}),

  /** El margen de aviso de los lotes y si un lote entra sin fecha. */
  lotSettings: () => api.get<LotSettings>('/inventory/lot-settings'),

  saveLotSettings: (command: {
    requiresLotExpiry: boolean;
    expiryAlertDays: number;
    /** Sin él, el servidor deja el que tenía. */
    immediateAlerts?: boolean;
    /** Ídem: sin él, se queda el que tenía. */
    saleMinDaysToExpiry?: number;
  }) => api.put<LotSettings>('/inventory/lot-settings', command),

  /**
   * La firma de quien responde por una excepción, con su PIN.
   *
   * Va antes de la operación y le devuelve el identificador que la operación
   * lleva. El PIN lo compara el servidor contra el personal del local: la
   * pantalla no tiene que preguntar quién va a firmar.
   */
  authorize: (command: { operationId: string; action: string; pin: string }) =>
    api.post<{ authorizationId: string; authorizedBy: { fullName: string } }>(
      '/auth/authorizations',
      command,
    ),

  registerWaste: (command: {
    operationId: string;
    supplyId: string;
    lotId?: string | null;
    quantity: string;
    reason: WasteReason;
    /** La firma, si el motivo la pide: ver `authorize`. */
    authorizationId?: string | null;
  }) =>
    api.post<{ movementId: string; balance: string; neededApproval: boolean }>(
      '/inventory/waste',
      { ...command, quantity: normalizarCantidad(command.quantity) },
    ),

  closeCount: (command: {
    operationId: string;
    document: string;
    lines: { supplyId: string; counted: string }[];
  }) =>
    api.post<{ adjustments: number; movements: string[] }>(
      '/inventory/counts',
      {
        ...command,
        lines: command.lines.map((line) => ({
          ...line,
          counted: normalizarCantidad(line.counted, 'Lo contado'),
        })),
      },
    ),

  purchaseOrders: () =>
    api
      .get<{ orders: PurchaseOrder[] }>('/inventory/purchase-orders')
      .then((r) => r.orders),

  createPurchaseOrder: (command: {
    orderId: string;
    supplierId: string;
    expectedAt?: string | null;
    notes?: string | null;
    lines: { supplyId: string; quantity: string; unitCost: number }[];
  }) =>
    api.post<{ orderId: string; number: string }>(
      '/inventory/purchase-orders',
      {
        ...command,
        lines: command.lines.map((line) => ({
          ...line,
          quantity: normalizarCantidad(line.quantity),
        })),
      },
    ),

  changeOrderStatus: (
    orderId: string,
    status: 'sent' | 'in_transit' | 'cancelled',
  ) => api.put<void>(`/inventory/purchase-orders/${orderId}/status`, { status }),

  suppliers: () =>
    api
      .get<{ suppliers: Supplier[] }>('/inventory/suppliers')
      .then((r) => r.suppliers),

  /** Los empaques de un insumo, todos de una vez: lo que no va se quita. */
  setPackages: (
    supplyId: string,
    packages: { id?: string; name: string; units: string; barcode: string | null }[],
  ) => api.put<void>(`/inventory/supplies/${supplyId}/packages`, { packages }),

  productions: () =>
    api
      .get<{ productions: Production[] }>('/inventory/productions')
      .then((r) => r.productions),

  produce: (command: {
    operationId: string;
    recipeId: string;
    batches: number;
  }) =>
    api.post<{
      /** Nulo si lo producido no va por lote. */
      lotId: string | null;
      quantity: string;
      unitCost: string;
      /** Lo que no alcanzaba: se produjo igual, pero el stock quedó en negativo. */
      shortIngredients: string[];
    }>('/inventory/productions', command),

  /** Devolver al proveedor lo que llegó mal. No es una merma. */
  returnToSupplier: (command: {
    operationId: string;
    supplyId: string;
    lotId?: string | null;
    quantity: string;
    document?: string | null;
  }) =>
    api.post<{ movementId: string; balance: string }>(
      '/inventory/returns',
      { ...command, quantity: normalizarCantidad(command.quantity) },
    ),

  /** Mandar mercadería a otro local. Sale de uno y entra en el otro a la vez. */
  transfer: (command: {
    operationId: string;
    supplyId: string;
    toBranchId: string;
    quantity: string;
    lotId?: string | null;
    document?: string | null;
  }) =>
    api.post<{
      movementId: string;
      originBalance: string;
      destinationBalance: string;
    }>('/inventory/transfers', {
      ...command,
      quantity: normalizarCantidad(command.quantity),
    }),

  /**
   * Sube la foto de un insumo.
   *
   * Tres pasos y el archivo **no pasa por la API**: se pide una dirección
   * firmada, se sube directo al almacén de imágenes y se confirma. Hacer de
   * intermediario de megabytes convertiría cada foto en el doble de tráfico y
   * en un tiempo de espera que se nota.
   *
   * La huella se calcula aquí, del contenido: dos personas que suban la misma
   * foto producen la misma clave, y volver a subir la que ya estaba no rompe
   * nada.
   */
  uploadSupplyImage: async (supplyId: string, archivo: File): Promise<void> => {
    const bytes = await archivo.arrayBuffer();
    const imageHash = await huellaDe(bytes);

    const { uploadUrl } = await api.post<{
      uploadUrl: string;
      expiresAt: string;
    }>(`/inventory/supplies/${supplyId}/image-upload`, { imageHash });

    const subida = await fetch(uploadUrl, {
      method: 'PUT',
      signal: AbortSignal.timeout(PLAZO_SUBIDA_MS),
      body: archivo,
      headers: { 'content-type': archivo.type || 'image/jpeg' },
    });

    if (!subida.ok) {
      throw new ApiError(
        subida.status,
        'IMAGE_UPLOAD_FAILED',
        'La foto no llegó al almacén de imágenes.',
      );
    }

    await api.put<void>(`/inventory/supplies/${supplyId}/image`, { imageHash });
  },

  removeSupplyImage: (supplyId: string) =>
    api.delete<void>(`/inventory/supplies/${supplyId}/image`),

  /** Lo que empezó a faltar y sigue faltando, con desde cuándo. */
  alerts: () =>
    api
      .get<{ alerts: InventoryAlert[] }>('/inventory/alerts')
      .then((r) => r.alerts),

  /**
   * El almacén de la cadena.
   *
   * `scope=all` pide todos los locales que quien pregunta puede ver. Las
   * sucursales no viajan en la petición: el servidor las calcula del usuario, y
   * esto solo dice «todos los que pueda».
   */
  chain: () => api.get<ChainStock>('/inventory/chain?scope=all'),

  /** Lo que se fue sin explicación. Pide `report.control`, no `inventory.view`. */
  /** El food cost real de un periodo, contra el de las recetas. */
  foodCost: (from: string, to: string) =>
    api.get<FoodCost>(`/inventory/food-cost?from=${from}&to=${to}`),

  shrinkage: (from: string, to: string) =>
    api.get<Shrinkage>(`/inventory/shrinkage?from=${from}&to=${to}`),

  /** Cuánto valía el almacén al cerrar un día. Sale de releer el kardex. */
  valuation: (at: string) =>
    api.get<Valuation>(`/inventory/valuation?at=${at}`),

  /** Qué comprar hoy, agrupado por proveedor y listo para firmarse. */
  restock: () =>
    api
      .get<{ groups: RestockGroup[] }>('/inventory/restock')
      .then((r) => r.groups),

  /** Qué comprar para un día según lo que se vendió los mismos días de antes. */
  marketList: (day?: string, weeks?: number) => {
    const filtros = new URLSearchParams();
    if (day !== undefined) filtros.set('day', day);
    if (weeks !== undefined) filtros.set('weeks', String(weeks));
    const consulta = filtros.toString();
    return api.get<MarketList>(`/inventory/market-list${consulta === '' ? '' : `?${consulta}`}`);
  },

  /** Qué pedir por lo que rota en los últimos `days` días, para `cover` días. */
  rotationList: (days: number, cover: number) =>
    api.get<RotationList>(`/inventory/rotation-list?days=${days}&cover=${cover}`),

  options: () => api.get<CatalogOptions>('/inventory/options'),

  products: () =>
    api
      .get<{ products: SellableProduct[] }>('/inventory/products')
      .then((r) => r.products),

  saveSupply: (command: {
    supplyId: string;
    /** Sin él, uno nuevo recibe el siguiente de su categoría: CAR-004. */
    sku?: string;
    name: string;
    categoryId: string;
    stockUnitId: string;
    purchaseUnitId?: string | null;
    purchaseFactor?: string | null;
    isLotTracked: boolean;
    /** Sus lotes entran con vencimiento aunque el negocio no lo exija. */
    requiresExpiry?: boolean;

    /** El impreso en el envase, para poder escanearlo. No es el SKU. */
    barcode?: string | null;

    preferredSupplierId?: string | null;
    minimum?: string;
    maximum?: string;
    location?: string | null;
    shelfLifeDays?: number | null;
  }) => {
    const { minimum, maximum, purchaseFactor, ...resto } = command;
    const minimo = cantidadOpcional(minimum, 'El mínimo');
    const maximo = cantidadOpcional(maximum, 'El máximo');

    return api.put<void>('/inventory/supplies', {
      ...resto,
      ...(minimo === undefined ? {} : { minimum: minimo }),
      ...(maximo === undefined ? {} : { maximum: maximo }),
      ...(purchaseFactor === undefined
        ? {}
        : { purchaseFactor: purchaseFactor === null ? null : normalizarCantidad(purchaseFactor, 'Lo que trae cada unidad de compra') }),
    });
  },

  saveUnit: (command: { unitId: string; code: string; name: string }) =>
    api.put<void>('/inventory/units', command),

  saveCategory: (command: { categoryId: string; name: string }) =>
    api.put<void>('/inventory/categories', command),

  saveSupplier: (command: {
    supplierId: string;
    name: string;
    taxId?: string | null;
    suppliesSummary?: string | null;
    leadTimeDays: number;
    paymentTerms?: string | null;
    minimumOrder?: string | null;
    status: 'preferred' | 'active' | 'observed';
    phone?: string | null;
    contactName?: string | null;
  }) => api.put<void>('/inventory/suppliers', command),

  adjust: (command: {
    operationId: string;
    supplyId: string;
    counted: string;
    reason: string;
  }) =>
    api.post<{ movementId: string; difference: string; balance: string }>(
      '/inventory/adjustments',
      { ...command, counted: normalizarCantidad(command.counted, 'Lo contado') },
    ),

  recipes: () =>
    api.get<{ recipes: Recipe[] }>('/inventory/recipes').then((r) => r.recipes),

  /** Los extras de la carta con su receta, si tienen. Migración 181. */
  extraRecipes: () =>
    api
      .get<{ extras: ExtraRecipe[] }>('/inventory/recipes/extras')
      .then((r) => r.extras),

  pendingRecipes: () =>
    api
      .get<{ pending: PendingRecipe[] }>('/inventory/recipes/pending')
      .then((r) => r.pending),

  /**
   * Vincula de una vez lo que se compra hecho: cada producto con su insumo y
   * una unidad por venta. Lo ya vinculado se salta y vuelve en
   * `alreadyLinked`.
   */
  linkResale: (command: {
    links: {
      productId: string;
      recipeId: string;
      newSupplyId: string;
      existingSupplyId?: string | null;
    }[];
    categoryId?: string | null;
    stockUnitId?: string | null;
  }) =>
    api.post<{
      linked: { productId: string; supplyId: string; createdSupply: boolean }[];
      alreadyLinked: string[];
    }>('/inventory/resale-links', command),

  saveRecipe: (command: {
    recipeId: string;
    /** El plato, o nulo si es una producción: ver `producesSupplyId`. */
    productId?: string | null;
    /** El insumo que produce una sub-receta, como la leche de tigre. */
    producesSupplyId?: string | null;
    /** El extra —«doble pollo»— cuya receta es. */
    modifierOptionId?: string | null;
    name: string;
    yieldQuantity: string;
    wastePercentage: number;
    lines: { supplyId: string; quantity: string }[];
  }) =>
    api.put<void>('/inventory/recipes', {
      ...command,
      yieldQuantity: normalizarCantidad(command.yieldQuantity, 'El rendimiento'),
      lines: command.lines.map((line) => ({
        ...line,
        quantity: normalizarCantidad(line.quantity, 'La cantidad de cada ingrediente'),
      })),
    }),

  // ── Retirar y devolver al catálogo ──────────────────────────────────────

  retired: () =>
    api.get<{
      supplies: { id: string; sku: string; name: string; unit: string; retiredAt: string }[];
      suppliers: { id: string; name: string; retiredAt: string }[];
    }>('/inventory/retired'),

  retireSupply: (supplyId: string) => api.delete<void>(`/inventory/supplies/${supplyId}`),
  restoreSupply: (supplyId: string) => api.post<void>(`/inventory/supplies/${supplyId}/restore`, {}),
  retireSupplier: (supplierId: string) => api.delete<void>(`/inventory/suppliers/${supplierId}`),
  restoreSupplier: (supplierId: string) =>
    api.post<void>(`/inventory/suppliers/${supplierId}/restore`, {}),

  // ── El conteo con su foto ───────────────────────────────────────────────

  /** Abre un conteo: guarda lo que dice el libro de cada insumo ahora. */
  startCount: (command: { countId: string; document: string; categoryId?: string | null; location?: string | null }) =>
    api.post<{ countId: string }>('/inventory/count-sessions', command),

  /** El conteo abierto, a ciegas: sin lo que dice el libro. */
  currentCount: () =>
    api.get<{ count: CountSession | null }>('/inventory/count-sessions/current').then((r) => r.count),

  /** `counted: null` deshace lo contado de ese insumo. */
  saveCountLines: (
    countId: string,
    lines: { supplyId: string; counted: string | null; lotId?: string | null }[],
  ) =>
    api.put<{ saved: number }>(`/inventory/count-sessions/${countId}/lines`, {
      lines: lines.map((line) => ({
        ...line,
        counted: line.counted === null ? null : normalizarCantidad(line.counted, 'Lo contado'),
      })),
    }),

  /** Las diferencias contra la foto, para recontar antes de cerrar. */
  reviewCount: (countId: string) => api.get<CountReview>(`/inventory/count-sessions/${countId}/review`),

  closeCountSession: (countId: string, operationId: string) =>
    api.post<{ adjustments: number; movements: string[] }>(`/inventory/count-sessions/${countId}/close`, {
      operationId,
    }),

  cancelCount: (countId: string) => api.delete<void>(`/inventory/count-sessions/${countId}`),

  /** Lo que no se manda —o va nulo— se queda como estaba en la ficha. */
  /**
   * A qué ficha va un código escaneado al recibir y cuántas unidades de ella
   * trae: la caja de seis, el blíster de diez, el código del distribuidor.
   * Nulo si nadie lo tiene aquí. Un servidor de antes no tiene la ruta:
   * también nulo, y la recepción se queda con lo que reconocía.
   */
  receivableByCode: (code: string) =>
    api
      .get<{ supplyId: string; unitsPerScan: string | null; via: string }>(
        `/inventory/receivable-by-code?code=${encodeURIComponent(code)}`,
      )
      .catch((error: unknown) => {
        if (error instanceof ApiError && error.status === 404) return null;
        throw error;
      }),

  setLimits: (
    supplyId: string,
    command: { minimum?: string | null; maximum?: string | null; location?: string | null; alertAtZero?: boolean },
  ) =>
    api.put<void>(`/inventory/supplies/${supplyId}/limits`, {
      ...(command.location === undefined ? {} : { location: command.location }),
      ...(command.alertAtZero === undefined ? {} : { alertAtZero: command.alertAtZero }),
      ...(command.minimum == null ? {} : { minimum: normalizarCantidad(command.minimum, 'El mínimo') }),
      ...(command.maximum == null ? {} : { maximum: normalizarCantidad(command.maximum, 'El máximo') }),
    }),
};

// ── Listas y escalas de precio (módulo `price_lists`, migración 250) ─────

export interface PriceList {
  id: string;
  name: string;
  customerCount: number;
  ruleCount: number;
}

export interface PriceRuleView {
  id: string;
  productId: string;
  productName: string;
  productPrice: string;
  priceListId: string | null;
  priceListName: string | null;
  minimumQuantity: string;
  unitPrice: string;
  /** Sin IGV, contra el costo promedio del almacén. Nulo sin costo. */
  marginPercent: string | null;
  belowMinimumMargin: boolean;
}

export interface MarginSettings {
  minimumMarginPercent: string | null;
  policy: 'warn' | 'block';
}

export interface MarginCheck extends MarginSettings {
  averageCost: string | null;
  marginPercent: string | null;
  belowMinimum: boolean;
}

export interface PricedCustomer {
  id: string;
  name: string;
  documentType: string | null;
  documentNumber: string | null;
  phone: string | null;
  priceListId: string;
}

export interface CustomerHit {
  id: string;
  name: string;
  documentNumber: string | null;
  phone: string | null;
}

export const precios = {
  lists: () => api.get<{ lists: PriceList[] }>('/price-lists').then((r) => r.lists),
  createList: (name: string) => api.post<PriceList>('/price-lists', { name }),
  deleteList: (id: string) => api.delete<void>(`/price-lists/${id}`),

  rules: () => api.get<{ rules: PriceRuleView[] }>('/price-lists/rules').then((r) => r.rules),
  saveRule: (command: {
    productId: string;
    priceListId: string | null;
    minimumQuantity: string;
    unitPrice: string;
  }) =>
    api.post<{ id: string; margin: MarginCheck }>('/price-lists/rules', {
      ...command,
      minimumQuantity: normalizarCantidad(command.minimumQuantity, 'La cantidad'),
    }),
  deleteRule: (id: string) => api.delete<void>(`/price-lists/rules/${id}`),

  margin: () => api.get<MarginSettings>('/price-lists/margin'),
  saveMargin: (settings: MarginSettings) => api.put<void>('/price-lists/margin', settings),

  customers: () =>
    api.get<{ customers: PricedCustomer[] }>('/price-lists/customers').then((r) => r.customers),
  assignCustomer: (customerId: string, priceListId: string | null) =>
    api.put<void>(`/price-lists/customers/${customerId}`, { priceListId }),

  /** Las fichas que nacieron de una venta: aquí solo se les pone su lista. */
  searchCustomers: (texto: string) =>
    api
      .get<{ customers: CustomerHit[] }>(`/customers?q=${encodeURIComponent(texto)}`)
      .then((r) => r.customers),
};

/**
 * El identificador de una operación que se puede repetir.
 *
 * Lo genera el cliente y viaja con cada escritura. Si el botón se pulsa dos
 * veces —o el navegador reintenta— el servidor reconoce el mismo identificador
 * y devuelve lo que ya hizo, en vez de hacerlo otra vez.
 */
export function newOperationId(): string {
  return crypto.randomUUID();
}
